import React, { useState, useEffect } from 'react';
import { 
    Button, Table, Card, Row, Col, Form, InputGroup, 
    Spinner, Modal, Badge, Pagination
} from 'react-bootstrap';
import { 
    collection, query, where, orderBy, getDocs, doc, 
    deleteDoc, updateDoc, serverTimestamp, getFirestore
} from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaSave, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import AreaForm from './AreaForm';

const AreasScreen = ({ usuario }) => {
    // Estados para gestionar la pantalla
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [currentArea, setCurrentArea] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Acceder a Firestore
    const db = getFirestore(firebaseApp);
    
    // Obtener RUC y nombre de la empresa del usuario autenticado
    const ruc = usuario?.ruc || '';
    const nombreEmpresa = usuario?.empresa || 'Empresa';

    // Cargar áreas al iniciar
    useEffect(() => {
        loadAreas();
    }, [ruc]);

    // Función para cargar áreas de la empresa
    const loadAreas = async () => {
        try {
            setLoading(true);
            const areasRef = collection(db, 'areas');
            const q = query(
                areasRef, 
                where('ruc', '==', ruc), 
                orderBy('nombre')
            );
            
            const snapshot = await getDocs(q);
            
            const areasList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            setAreas(areasList);
        } catch (error) {
            console.error("Error cargando áreas:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar las áreas. Por favor, inténtelo nuevamente.'
            });
        } finally {
            setLoading(false);
        }
    };

    // Filtrar áreas según término de búsqueda
    const filteredAreas = areas.filter(area => 
        area.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (area.ceco && area.ceco.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Calcular paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentAreas = filteredAreas.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredAreas.length / itemsPerPage);

    // Cambiar de página
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Abrir modal para editar
    const handleEdit = (area) => {
        setCurrentArea(area);
        setShowModal(true);
    };

    // Abrir modal para crear nueva área
    const handleNew = () => {
        setCurrentArea(null);
        setShowModal(true);
    };

    // Eliminar un área
    const handleDelete = async (areaId, nombreArea) => {
        try {
            const result = await Swal.fire({
                title: '¿Está seguro?',
                text: `Va a eliminar el área "${nombreArea}". Esta acción no se puede deshacer.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#dc3545',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                await deleteDoc(doc(db, 'areas', areaId));
                
                Swal.fire(
                    'Eliminada',
                    'El área ha sido eliminada correctamente.',
                    'success'
                );
                
                // Actualizar lista local sin necesidad de recargar de Firestore
                setAreas(prevAreas => prevAreas.filter(area => area.id !== areaId));
            }
        } catch (error) {
            console.error("Error eliminando área:", error);
            
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo eliminar el área. Es posible que esté siendo utilizada por otras entidades del sistema.'
            });
        }
    };

    // Guardar un área (nueva o edición)
    const handleSaveArea = async (formData) => {
        try {
            if (currentArea) {
                // Actualizar área existente
                await updateDoc(doc(db, 'areas', currentArea.id), {
                    ...formData,
                    fechaActualizacion: serverTimestamp()
                });
                
                // Actualizar lista local
                setAreas(prevAreas => 
                    prevAreas.map(area => 
                        area.id === currentArea.id 
                            ? { ...area, ...formData } 
                            : area
                    )
                );
                
                Swal.fire('Actualizada', 'Área actualizada correctamente', 'success');
            } else {
                // La creación se maneja en el componente AreaForm
                loadAreas(); // Recargar para obtener el área recién creada
                Swal.fire('Creada', 'Área creada correctamente', 'success');
            }
            
            setShowModal(false);
        } catch (error) {
            console.error("Error guardando área:", error);
            
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Ocurrió un error al guardar el área.'
            });
        }
    };

    return (
        <div className="p-1">
            <Row className="mb-4 align-items-center">
                <Col md={8}>
                    <h4 className="mb-0">Gestión de Áreas y Departamentos</h4>
                    <p className="text-muted mb-0">
                        Administre las áreas o departamentos de su empresa
                    </p>
                </Col>
                <Col md={4} className="text-end">
                    <Button 
                        variant="primary"
                        onClick={handleNew}
                        className="d-flex align-items-center ms-auto"
                    >
                        <FaPlus className="me-2" /> Nueva Área
                    </Button>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Buscar por nombre o CECO..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1); // Resetear a primera página
                                    }}
                                />
                                {searchTerm && (
                                    <Button 
                                        variant="outline-secondary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <FaTimes />
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={6} className="text-end text-muted">
                            {filteredAreas.length} áreas encontradas
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Cargando áreas...</p>
                        </div>
                    ) : currentAreas.length === 0 ? (
                        <div className="text-center py-4">
                            <p className="mb-0 text-muted">
                                {searchTerm ? 'No se encontraron áreas que coincidan con la búsqueda.' : 'No hay áreas registradas.'}
                            </p>
                            {searchTerm && (
                                <Button 
                                    variant="link" 
                                    onClick={() => setSearchTerm('')}
                                >
                                    Limpiar búsqueda
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            <Table responsive hover>
                                <thead className="bg-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Nombre del Área</th>
                                        <th>CECO</th>
                                        <th>Fecha de Creación</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentAreas.map((area, index) => (
                                        <tr key={area.id}>
                                            <td>{indexOfFirstItem + index + 1}</td>
                                            <td>{area.nombre}</td>
                                            <td>
                                                {area.ceco ? (
                                                    <Badge bg="info" className="text-white">
                                                        {area.ceco}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted">—</span>
                                                )}
                                            </td>
                                            <td>
                                                {area.fechaCreacion ? 
                                                    new Date(area.fechaCreacion.seconds * 1000).toLocaleDateString() : 
                                                    'N/A'}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline-primary"
                                                        onClick={() => handleEdit(area)}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline-danger"
                                                        onClick={() => handleDelete(area.id, area.nombre)}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <Pagination>
                                        <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
                                        
                                        {[...Array(totalPages)].map((_, i) => (
                                            <Pagination.Item 
                                                key={i + 1} 
                                                active={i + 1 === currentPage}
                                                onClick={() => paginate(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        ))}
                                        
                                        <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
                                        <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal para crear/editar área */}
            <Modal 
                show={showModal} 
                onHide={() => setShowModal(false)}
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {currentArea ? 'Editar Área' : 'Nueva Área'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AreaForm 
                        area={currentArea}
                        onSave={handleSaveArea}
                        onCancel={() => setShowModal(false)}
                        ruc={ruc}
                        empresa={nombreEmpresa}
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AreasScreen;